import { ScoreService } from "src/services/ScoreService";
import { Post, PostElement } from "src/api/client-api/post/postSchema";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { Campaign } from "src/api/client-api/useCampaign";
import { Field } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { formatUnitAbbreviation } from "src/utils/formatting";
import { PropertiesTree } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { DimensionValue, NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

export type PropertyValue = {
    type: "value";
    fieldType: Field["type"];
    value: string;
    unit: string | undefined;
    score: NumericalScoreValue | null;
    dataListOptionCodes: string[];
    isGenericValue: boolean;

    isSubpropertyOverriden: false;

    // Used only for multivalues;
    position?: number;

    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};
export type PropertyGroup = {
    type: "group";
    content: PropertyValue[];

    isSubpropertyOverriden: boolean;

    score: NumericalScoreValue | null;
    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};

type PropertyMultiScore = {
    title: string;
    name?: string;
    score: NumericalScoreValue | null;
};
export type PropertyMulti = {
    type: "multi";
    content: PropertyValue[][];

    isSubpropertyOverriden: boolean;

    scoreBySubproperty: PropertyMultiScore[];
    score: NumericalScoreValue | null;

    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};
export type PropertyConditional = PropertyValue | PropertyGroup | PropertyMulti;

// This is the main Element of the magic sauce.
// It contains 1 question (ssq_property) + 1 answer (element) + 1 score
// And the pair of Element + Score varies depending on the Question.Type.
// export type Property = PropertyConditional & PropertyCore;
export type Property = PropertyConditional;

// Those are the properties the user face to make
// their aggregations;
type ExtraKeys<T> = { extraIndexes: T };

export type SsqPropertyWithIndexesKey<T> = SsqProperty & ExtraKeys<T>;
export type UserProperty<T> = Property & ExtraKeys<T>;
export type ConstructorOptions<T> = {
    listOfProperties: SsqPropertyWithIndexesKey<T>[];
    propertiesScore: DimensionValue[];
    postElementAndCampaign: Pick<Post, "id" | "campaign" | "elements">;
    propertiesTree?: PropertiesTree;
};

export class PropertyBuilder<GenericKeys extends Record<keyof GenericKeys, number | string>> {
    _properties: UserProperty<GenericKeys>[];
    _initOptions: ConstructorOptions<GenericKeys>;
    _propertiesTree?: PropertiesTree;

    public constructor(options: ConstructorOptions<GenericKeys>) {
        const { listOfProperties, propertiesTree } = options;
        this._initOptions = options;
        this._propertiesTree = propertiesTree;

        const formatted = listOfProperties.flatMap((property) => {
            if (property.hidden === true) {
                return [];
            }
            const { extraIndexes: _, ...ssqProperty } = property;
            const validProperty = this.buildBaseProperty(ssqProperty);

            const result: UserProperty<GenericKeys> = {
                extraIndexes: property.extraIndexes,
                ...validProperty,
            };
            return [result];
        });
        this._properties = formatted;
    }

    public build(): UserProperty<GenericKeys>[] {
        return [...this._properties];
    }

    private extractMetadataFromProperty(property: SsqProperty) {
        const titleFallback = "";
        const nameFallback = property.title ?? "";

        return {
            title: property.title ?? titleFallback,
            name: property.name ?? nameFallback,
        };
    }

    private buildBaseProperty(property: SsqProperty) {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;

        // Multi should be a `field.type` but isn't today...
        const multiKey = "multi" as const;
        const groupKey = "group" as const;
        const valueKey = "value" as const;

        // Most important condition of the Property feature
        // The main problem with this is that we would love to use `.field.type`
        // But the data is inconsistent;
        const whichPropertyIsIt =
            property.multivalue === true ? multiKey
            : (property.children ?? []).length > 0 ? groupKey
            : valueKey;

        const elementFound = elements?.find((element) => {
            return element.ssqPropertyId === property.id;
        });
        const scoreProperty = propertiesScore.find((dimensionValue) => dimensionValue.id === property.id.toString());

        if (whichPropertyIsIt === "value") return this.buildValueProperty(property, elementFound, scoreProperty);
        if (whichPropertyIsIt === "group") return this.buildGroupProperty(property);
        if (whichPropertyIsIt === "multi") return this.buildMultiProperty(property);

        // I would suggest to have an automatic handler that sends
        // throwed Errors to Sentry immediately, and simply restart the App
        // with a Snackbar message to notify user something went wrong.
        // But we have started an investigation automatically.

        // And here, I would take the bet this error never arrives.
        // But if it does, I'm notified on Sentry. And I have a graceful termination
        throw new Error("buildBaseProperty went wrong");
    }

    private buildValueProperty(
        property: SsqProperty,
        element: PostElement | undefined,
        scoreProperty: DimensionValue | undefined,
    ): PropertyValue {
        const propertyMeta = this.extractMetadataFromProperty(property);

        if (element === undefined) {
            return {
                // Context Data
                postId: this._initOptions.postElementAndCampaign.id,

                property: property,
                title: propertyMeta.title,
                name: propertyMeta.name,
                isSubpropertyOverriden: false,

                isGenericValue: false,
                campaign: this._initOptions.postElementAndCampaign.campaign,
                type: "value",
                fieldType: "text",
                value: "",
                score: null,
                unit: "",
                dataListOptionCodes: [],
            };
        }

        const fieldType = property.field?.type as Field["type"] | undefined;
        const defaultType = "text" as const;

        return {
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,
            isSubpropertyOverriden: false,

            // Meta Data
            type: "value",
            property: property,
            name: propertyMeta.name,
            title: propertyMeta.title,

            fieldType: fieldType ?? defaultType,
            unit: formatUnitAbbreviation(property.unit),

            // Data
            value: element.value,
            dataListOptionCodes: element.dataListOptionCodes ?? [],
            isGenericValue: element.isGenericValue ?? false,
            position: element.position,

            // Results
            score: OlapScoresService.normalizeNumericalScore(scoreProperty?.score.value),
        };
    }

    /*  This is a special case because back-end needs the Front to aggregate the data
        When this special case happens.
        The special case :
            -   Whenever we provide a Property Tree attribute to the PropertyBuilder
                We want to override the score of the Parent Group/Multi, so that it's only
                the sum of the selected subset.

        Backend should do it, but it's not ready yet.
    */
    private evaluateSpecialPropertyTreeRule = (options: { propertyId: number }) => {
        const { propertyId } = options;

        const existingPropertyTree = this._propertiesTree?.[propertyId];
        const doesTreeExist = existingPropertyTree !== undefined;
        const isTreeValid = Array.isArray(existingPropertyTree) && existingPropertyTree.length > 0;
        const isSubpropertyOverriden = doesTreeExist && isTreeValid;

        return { isSubpropertyOverriden, existingPropertyTree };
    };

    private evaluateShouldSkipProperty = (options: {
        subpropertyId: number;
        isSubpropertyOverriden: boolean;
        existingPropertyTree: number[] | undefined;
    }): boolean => {
        const { subpropertyId, existingPropertyTree, isSubpropertyOverriden } = options;
        const isSubpropertyFound = existingPropertyTree?.includes(subpropertyId);
        if (isSubpropertyOverriden) {
            if (isSubpropertyFound === false) {
                return true;
            }
        }

        return false;
    };

    private buildGroupProperty(property: SsqProperty): PropertyGroup {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;
        const propertyMeta = this.extractMetadataFromProperty(property);

        let temporaryPropertiesTreeScore: NumericalScoreValue = null;
        const { isSubpropertyOverriden, existingPropertyTree } = this.evaluateSpecialPropertyTreeRule({
            propertyId: property.id,
        });

        const propertyScore = propertiesScore.find((dimensionValue) => dimensionValue.id === property.id.toString());
        const data = property.children?.flatMap((childProperty) => {
            if (childProperty.hidden === true) {
                return [];
            }
            const shouldSkip = this.evaluateShouldSkipProperty({
                subpropertyId: childProperty.id,
                existingPropertyTree,
                isSubpropertyOverriden,
            });
            if (shouldSkip) {
                return [];
            }
            const elementFromChildren = elements?.find((element) => {
                return element.ssqPropertyId === childProperty.id;
            });

            const scoreDrilldown = OlapScoresService.getDimension("subproperties", propertyScore?.dimensions);
            const subpropertyScore = OlapScoresService.getDimensionValue(childProperty.id, scoreDrilldown);
            const valueProperty = this.buildValueProperty(childProperty, elementFromChildren, subpropertyScore);

            temporaryPropertiesTreeScore = ScoreService.addScore(
                temporaryPropertiesTreeScore,
                valueProperty.score ?? null,
            );

            return [valueProperty];
        });

        const propertyScoreValue = OlapScoresService.normalizeNumericalScore(propertyScore?.score.value);
        const score = isSubpropertyOverriden ? temporaryPropertiesTreeScore : propertyScoreValue;
        return {
            type: "group",
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,
            isSubpropertyOverriden,

            // Meta Data
            property: property,
            title: propertyMeta.title,
            name: propertyMeta.name,

            // Data
            content: data ?? [],

            // Results
            score,
        };
    }

    private buildMultiProperty(property: SsqProperty): PropertyMulti {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;
        const propertyMeta = this.extractMetadataFromProperty(property);

        let temporaryPropertiesTreeScore: NumericalScoreValue = null;
        const { isSubpropertyOverriden, existingPropertyTree } = this.evaluateSpecialPropertyTreeRule({
            propertyId: property.id,
        });

        const propertyScore = propertiesScore.find((dimensionValue) => dimensionValue.id === property.id.toString());

        const allSubpropertiesIds = property.children?.map((propertyChild) => propertyChild.id);
        const allElementsBySubpropertiesIds = elements?.filter((element) =>
            allSubpropertiesIds?.includes(element.ssqPropertyId),
        );
        const elementsByPosition = Object.groupBy(allElementsBySubpropertiesIds ?? [], (elem) => elem.position);

        const allPossiblePositions = [
            ...new Set((allElementsBySubpropertiesIds ?? []).map((element) => element.position)),
        ];

        const groupedScoreByIdOnly = OlapScoresService.getDimension("subproperties", propertyScore?.dimensions);
        const data: PropertyValue[][] = allPossiblePositions.map((position) => {
            const _elements = elementsByPosition[position];
            const groupedValuesForMulti: PropertyValue[] =
                property.children?.flatMap((subproperty) => {
                    const shouldSkip = this.evaluateShouldSkipProperty({
                        subpropertyId: subproperty.id,
                        existingPropertyTree,
                        isSubpropertyOverriden,
                    });
                    if (shouldSkip) {
                        return [];
                    }

                    const _groupedScoreByPosition = groupedScoreByIdOnly?.values.find(
                        (dimensionValue) => dimensionValue.id === subproperty.id.toString(),
                    );
                    const groupedScoreByPosition = OlapScoresService.getDimension(
                        "multivalues",
                        _groupedScoreByPosition?.dimensions,
                    );

                    const scoreForMulti = groupedScoreByPosition?.values.find(
                        (dimensionValue) => dimensionValue.id === (position + 1).toString(),
                    );
                    const foundElementInsideMulti = _elements?.find((elem) => elem.ssqPropertyId === subproperty.id);
                    const propertyValue = this.buildValueProperty(subproperty, foundElementInsideMulti, scoreForMulti);

                    if (existingPropertyTree?.includes(Number(subproperty.id))) {
                        temporaryPropertiesTreeScore = ScoreService.addScore(
                            temporaryPropertiesTreeScore,
                            propertyValue.score ?? null,
                        );
                    }

                    return [propertyValue];
                }) ?? [];
            return groupedValuesForMulti;
        });

        const scoreBySubproperty =
            property.children?.map((subProperty) => {
                const subPropertyScore = groupedScoreByIdOnly?.values.find(
                    (dimensionValue) => dimensionValue.id === subProperty.id.toString(),
                );
                const item: PropertyMultiScore = {
                    score: OlapScoresService.normalizeNumericalScore(subPropertyScore?.score.value),
                    title: subProperty.title ?? "",
                    name: subProperty.name,
                };
                return item;
            }) ?? [];

        const propertyScoreValue = OlapScoresService.normalizeNumericalScore(propertyScore?.score.value);
        const score = isSubpropertyOverriden ? temporaryPropertiesTreeScore : propertyScoreValue;

        return {
            type: "multi",
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,
            isSubpropertyOverriden,

            // Meta Data
            property: property,
            name: propertyMeta.name,
            title: propertyMeta.title,

            // Data
            content: data,

            // Results
            score,
            scoreBySubproperty: scoreBySubproperty,
        };
    }
}
